import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ApplicationContextStore, AuthService, BranchService, NamedRoutes, RouterService, ToastService } from '@raptor-fe/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { WebSelectBranchModalStore } from '@raptor-fe/ui';

@Component({
    selector: 'marlin-web-welcome-screen',
    templateUrl: './welcome-screen.component.html',
    styleUrls: ['./welcome-screen.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, FormsModule],
})
export class WelcomeScreenComponent implements OnInit {
    protected readonly applicationContextStore = inject(ApplicationContextStore);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);
    private readonly branchService = inject(BranchService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly webAlertService = inject(ToastService);
    private readonly webSelectBranchModalStore = inject(WebSelectBranchModalStore);

    protected readonly isLoaded = signal(false);

    ngOnInit(): void {
        const redirectRoute = this.activatedRoute.snapshot.queryParams['redirectRoute'] || null;
        const branchId = this.activatedRoute.snapshot.queryParams['branchId'] || null;

        this.branchService
            .getAll(this.applicationContextStore.tenantId(), true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (branchResponse) => {
                    this.isLoaded.set(true);
                    if (branchResponse.length === 1) {
                        this.applicationContextStore.setSingleBranchUser(branchResponse[0]);

                        setTimeout(() => {
                            this.navigateBasedOnRedirect(redirectRoute);
                        }, 1500);
                    } else {
                        const branch = branchId
                            ? (branchResponse.find((b) => b.id === branchId) ?? branchResponse[0])
                            : (branchResponse.find((b) => b.id === this.applicationContextStore.branch()?.id) ?? branchResponse[0]);

                        this.applicationContextStore.setBranch(branch);

                        if (branchId) {
                            this.navigateBasedOnRedirect(redirectRoute);
                        } else {
                            setTimeout(() => {
                                this.webSelectBranchModalStore.open(redirectRoute);
                            }, 500);
                        }
                    }
                },
                error: () => {
                    this.isLoaded.set(true);
                    this.webAlertService.showError('An unexpected error occurred while logging in');
                    this.authService.logout();
                    this.routerService.navigateAndReplaceUrl(NamedRoutes.login());
                },
            });
    }

    private navigateBasedOnRedirect(redirectRoute: string | null): void {
        if (redirectRoute) {
            this.router.navigateByUrl(redirectRoute);
        } else {
            this.routerService.navigateTo(NamedRoutes.landing());
        }
    }
}
